/**
 * R25 — the §38 `card-modal` open/close client-message wire shape.
 *
 * Origin (the "Shashank Desai" session, conv `47aef98e…`): during the card-scan flow the mic
 * transcribed the kiosk's OWN on-screen labels / spoken prompts ("Take photo", "Capture",
 * "Card saved" ×4) as visitor turns — polluting the transcript and the LLM context. The kiosk now
 * tells the server whenever a card-scan SURFACE (the source sheet, the in-app camera modal, or a
 * distinct crop/confirm step) is on screen, so the server can suppress STT / ignore turns for the
 * duration of the scan. A LOCAL mic pause (via the established mute path) is the belt; this signal is
 * the braces (the server half is concierge-gg R47 — voice-capture-contract §37, running in PARALLEL).
 *
 * `sendClientMessage(type, data)` serialises to `{ type, data }`, so the wire message is:
 *   { "type": "card-modal", "data": { "open": true|false } }
 *
 * This pure helper builds the `data` payload (and the full message) so the exact wire shape is
 * unit-testable against the frozen fixture WITHOUT a React/RTVI harness (the kiosk suite has no
 * jsdom). `useVoice.setScanSurfaceActive` calls
 * `client.sendClientMessage(CARD_MODAL_MSG_TYPE, buildCardModalData(open))`.
 *
 * ⚑ SHAPE NOTE (R25): the §37/§38 shape is being FROZEN by concierge-gg R47 in parallel and is NOT
 * yet reconciled. `{type:'card-modal', data:{open:boolean}}` is the CANONICAL STAND-IN named in the
 * R25 handoff — cross-verify against the frozen §37 shape when R47 lands (see §Needs-Andy / the
 * fixture `r47-card-modal-open.json` ⚑ stand-in).
 */

/** The RTVI client-message type for a card-scan surface open/close (§38). */
export const CARD_MODAL_MSG_TYPE = 'card-modal' as const;

/** Build the `data` payload for a `card-modal` message: `{ open }`. */
export function buildCardModalData(open: boolean): { open: boolean } {
  return { open };
}

/**
 * Build the FULL wire message a `sendClientMessage('card-modal', data)` produces — used by the tests
 * to assert the signal emits the frozen §38 shape verbatim. Kept here (not just in the test) so the
 * canonical shape lives in one place next to the type + data builder.
 */
export function buildCardModalMessage(open: boolean): {
  type: typeof CARD_MODAL_MSG_TYPE;
  data: { open: boolean };
} {
  return { type: CARD_MODAL_MSG_TYPE, data: buildCardModalData(open) };
}

/**
 * The mic action the scan gate must apply on a surface transition — extracted PURE so the R21-safe
 * gating rules (established mute path; restore prior intent; never un-pause) are unit-testable without
 * a React/RTVI harness. `useVoice.setScanSurfaceActive` calls this and applies the result via the
 * established `enableMic` + `setMic` path (NEVER raw track toggles — the mic-track-churn lesson).
 */
export type ScanMicAction =
  /** No mic change (already in the target state / nothing to restore). */
  | { mic: 'none' }
  /** Mute the mic for the scan (established mute path) — the visitor's prior intent was ENABLED. */
  | { mic: 'mute' }
  /** Restore the mic to ENABLED on scan close — the scan had muted it and no manual pause intervened. */
  | { mic: 'unmute' };

/**
 * Resolve the mic action on a scan-surface OPEN edge, given whether the mic is currently enabled.
 *  - mic enabled  → mute for the scan (remember the prior intent = enabled, to restore on close).
 *  - mic disabled → nothing to do (already silent; a manual mute/pause must survive the scan).
 */
export function scanMicActionOnOpen(micEnabled: boolean): ScanMicAction {
  return micEnabled ? { mic: 'mute' } : { mic: 'none' };
}

/**
 * Resolve the mic action on a scan-surface CLOSE edge (incl. cancel).
 *  - `micBeforeScan` is the intent captured at open (`null` when open never muted anything).
 *  - `paused` is the CURRENT store pause: if the visitor paused the whole exchange DURING the scan,
 *    close must NOT un-pause (the manual pause wins — never fight the visitor's explicit intent).
 * Restore only when the scan actually muted an enabled mic AND no pause intervened.
 */
export function scanMicActionOnClose(
  micBeforeScan: boolean | null,
  paused: boolean
): ScanMicAction {
  const shouldRestore = micBeforeScan === true;
  if (shouldRestore && !paused) return { mic: 'unmute' };
  return { mic: 'none' };
}
